import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useSyncExternalStore,
} from "react";
import i18n from "i18next";
import { ReactReduxContext } from "react-redux";
import { setGuideTag } from "../../../common/actions";
import "../../../_editor/components/canvas/editor_box/screen_training/_screen_training.scss";

const EMPTY_TAGS = {};

function itemUnit(item) {
  return item && item.unit === "px" ? "px" : "%";
}

function itemStyle(item) {
  const u = itemUnit(item);
  return {
    left: item.x + u,
    top: item.y + u,
    width: item.width + u,
    height: item.height + u,
  };
}

function validate(item, raw) {
  if (item.kind !== "input") return true;
  const entered = (raw || "").trim();
  const v = item.validation;
  if (!v) return entered.length > 0;
  if (v.mode === "exact") {
    return entered.toLowerCase() === (v.value || "").trim().toLowerCase();
  }
  if (v.mode === "random") {
    if (v.minLength != null && entered.length < v.minLength) return false;
    if (v.maxLength != null && entered.length > v.maxLength) return false;
    return entered.length > 0;
  }
  return entered.length > 0;
}

export default function GuideMultiInputRenderer({
  items,
  visor,
  advanceOnComplete = true,
  guidedCue,
  zoomEnabled = false,
  onAdvance,
  onNavigate,
  setTag,
  tagValues,
  onItemsUpdate,
}) {
  const list = Array.isArray(items) ? items : [];
  const reduxCtx = useContext(ReactReduxContext);
  const reduxStore = reduxCtx ? reduxCtx.store : null;

  // Subscribe to Redux when a Provider is present; otherwise to a CustomEvent.
  const subscribe = useCallback(
    (onChange) => {
      if (reduxStore) {
        return reduxStore.subscribe(onChange);
      }
      window.addEventListener("aidea:tag-set", onChange);
      return () => window.removeEventListener("aidea:tag-set", onChange);
    },
    [reduxStore]
  );

  const getSnapshot = useCallback(() => {
    if (reduxStore) {
      const s = reduxStore.getState();
      return (s && s.guideTagValues) || EMPTY_TAGS;
    }
    if (typeof window !== "undefined") {
      if (!window.IdealStudio) window.IdealStudio = {};
      if (!window.IdealStudio.guideTagValues) window.IdealStudio.guideTagValues = {};
      return window.IdealStudio.guideTagValues;
    }
    return EMPTY_TAGS;
  }, [reduxStore]);

  const reduxTagValues = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const effectiveTagValues = tagValues || reduxTagValues;

  const dispatchSetTag = useCallback(
    (tag, value) => {
      if (reduxStore) {
        reduxStore.dispatch(setGuideTag(tag, value));
        return;
      }
      if (typeof window === "undefined") return;
      if (!window.IdealStudio) window.IdealStudio = {};
      const next = { ...(window.IdealStudio.guideTagValues || {}), [tag]: value };
      window.IdealStudio.guideTagValues = next;
      try {
        window.dispatchEvent(new CustomEvent("aidea:tag-set", { detail: { tag, value } }));
      } catch (_) {
        const ev = document.createEvent("CustomEvent");
        ev.initCustomEvent("aidea:tag-set", true, true, { tag, value });
        window.dispatchEvent(ev);
      }
    },
    [reduxStore]
  );
  const effectiveSetTag = typeof setTag === "function" ? setTag : dispatchSetTag;
  const [statuses, setStatuses] = useState({});
  const isInteractive = (item) => item && item.kind !== "tag";
  const firstInteractiveIdx = (from) => {
    for (let i = from; i < list.length; i++) if (isInteractive(list[i])) return i;
    return -1;
  };
  // Tag items are display-only: they must never hold the active slot,
  // otherwise the sequence deadlocks on them.
  const [activeIdx, setActiveIdx] = useState(() => firstInteractiveIdx(0));
  const [values, setValues] = useState({});
  const [dragIdx, setDragIdx] = useState(null); // index of item currently being dragged
  const [selectedIdx, setSelectedIdx] = useState(null); // editor: clicked item shows a delete (×) handle
  const containerRef = useRef(null);
  const advanceTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(advanceTimerRef.current), []);

  const cue = { beacon: true, cursor: true, backdrop: false, ...(guidedCue || {}) };
  const activeItem = visor && activeIdx >= 0 && activeIdx < list.length ? list[activeIdx] : null;
  const cursorPos = activeItem
    ? {
        left: activeItem.x + (activeItem.width || 0) / 2 + itemUnit(activeItem),
        top: activeItem.y + (activeItem.height || 0) / 2 + itemUnit(activeItem),
      }
    : null;

  // Step-triggered camera: zoom the scene canvas (screenshot + this overlay
  // live in the same .innercanvas, so they stay aligned) toward the active
  // item. We hold the FULL view for an establishing beat first — the
  // beacon/cursor already point at the target, so the learner can locate it
  // before the camera closes in (Screen-Studio style). Without this the scene
  // loaded already zoomed and the learner couldn't see where the element was.
  useEffect(() => {
    if (!visor || !zoomEnabled) return undefined;
    const canvas = containerRef.current ? containerRef.current.closest(".innercanvas") : null;
    if (!canvas) return undefined;
    const airlayer = canvas.parentElement;
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let establishTimer = null;
    const reset = () => {
      clearTimeout(establishTimer);
      canvas.style.transform = "";
      canvas.style.transformOrigin = "";
      canvas.style.transition = "";
      if (airlayer) airlayer.style.overflow = "";
    };
    if (!activeItem || itemUnit(activeItem) === "px") {
      // ponytail: px-unit items skip zoom (importer always emits %); convert if ever needed
      reset();
      return reset;
    }
    // Frame the active item. Pinning transform-origin at the item's own
    // center works mid-scene, but an item hugging an edge gets its far half
    // scaled past the canvas edge and clipped (airlayer overflow:hidden) — it
    // "en sağda/en solda tam gözükmüyor". Clamp the origin into the range that
    // keeps BOTH item edges inside the zoomed viewport (then into 0-100%).
    // Middle items are unchanged.
    const ZOOM_SCALE = 1.6;
    const win = 100 / ZOOM_SCALE; // visible source window width (%)
    const k = 1 - 1 / ZOOM_SCALE; // source % at the screen edge per 1% origin
    const originFor = (center, half) => {
      const hi = (center - half) / k; // keep the near edge in view
      const lo = (center + half - win) / k; // keep the far edge in view
      const o =
        lo > hi
          ? (center - win / 2) / k // item wider than viewport → best-effort center
          : Math.min(Math.max(center, lo), hi);
      return Math.min(Math.max(o, 0), 100);
    };
    const ox = originFor(activeItem.x + (activeItem.width || 0) / 2, (activeItem.width || 0) / 2);
    const oy = originFor(activeItem.y + (activeItem.height || 0) / 2, (activeItem.height || 0) / 2);
    if (airlayer) airlayer.style.overflow = "hidden";
    canvas.style.transition = reduced
      ? "none"
      : "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform-origin 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
    // Pre-aim the origin (invisible while at scale 1), show the full view,
    // then zoom in after the establishing hold.
    canvas.style.transformOrigin = ox + "% " + oy + "%";
    canvas.style.transform = "scale(1)";
    const zoomIn = () => {
      canvas.style.transform = "scale(" + ZOOM_SCALE + ")";
    };
    if (reduced) zoomIn();
    else establishTimer = setTimeout(zoomIn, 700);
    return reset;
  }, [visor, zoomEnabled, activeItem]);

  const getState = (idx) => {
    if (!visor) return "active";
    const item = list[idx];
    if (!item) return "locked";
    if (item.kind === "tag") return "active";
    if (statuses[item.id] === "correct") return "done";
    if (activeIdx === -1 || idx < activeIdx) return "done";
    if (idx === activeIdx) return "active";
    return "locked";
  };

  // Move to the next interactive item; when none is left, the scene is
  // complete → fire onAdvance (aidea:advance-scene via VisorBox) unless the
  // author turned advanceOnComplete off. Small delay so the learner sees the
  // final item flip to "done" before the scene changes.
  const advance = () => {
    const next = firstInteractiveIdx(activeIdx + 1);
    setActiveIdx(next === -1 ? list.length : next);
    if (next === -1 && advanceOnComplete && typeof onAdvance === "function") {
      advanceTimerRef.current = setTimeout(() => {
        // Visor keeps scenes mounted and hides them with a .hidden class
        // (VisorCanvasSli). If the learner already navigated away during the
        // delay, firing onAdvance here would skip an extra scene.
        const el = containerRef.current;
        if (el && el.closest && el.closest(".hidden")) return;
        onAdvance();
      }, 450);
    }
  };

  // If an item carries its own navigation (onClickAction === "step" with a
  // remapped targetStepId), completing it jumps to that scene instead of
  // advancing to the next item in the sequence.
  const resolveItemAction = (item) => {
    if (item && item.onClickAction === "step" && item.targetStepId) {
      return { kind: "navigate", to: item.targetStepId };
    }
    return { kind: "advance" };
  };

  const applyItemAction = (item) => {
    const action = resolveItemAction(item);
    if (action.kind === "navigate" && typeof onNavigate === "function") {
      onNavigate(action.to);
      return;
    }
    advance();
  };

  const handleClickItem = (idx) => {
    if (!visor || getState(idx) !== "active") return;
    const item = list[idx];
    setStatuses((s) => ({ ...s, [item.id]: "correct" }));
    applyItemAction(item);
  };

  const handleInputSubmit = (idx) => {
    if (!visor || getState(idx) !== "active") return;
    const item = list[idx];
    const raw = values[item.id] || "";
    if (validate(item, raw)) {
      setStatuses((s) => ({ ...s, [item.id]: "correct" }));
      if (item.tag) effectiveSetTag(item.tag, raw.trim());
      applyItemAction(item);
    } else {
      setStatuses((s) => ({ ...s, [item.id]: "wrong" }));
      setTimeout(() => setStatuses((s) => ({ ...s, [item.id]: "idle" })), 400);
    }
  };

  // ─── Editor-mode drag ─────────────────────────────────────────────────
  // We capture pointerdown on the overlay itself, then listen for global
  // mousemove/mouseup. Position is computed as a % of the container rect.
  // Editor: remove an item. Mirrors GuideMultiInputProperties.onDelete —
  // drop it from the items array and let the parent persist. Clears selection
  // so the (now stale) × handle disappears.
  const deleteItem = (idx) => {
    setSelectedIdx(null);
    if (onItemsUpdate) onItemsUpdate(list.filter((_, i) => i !== idx));
  };

  const beginDrag = (idx, e) => {
    if (visor) return;
    e.stopPropagation();
    e.preventDefault();
    // Clicking (or grabbing) an item selects it → reveals its × delete handle.
    setSelectedIdx(idx);

    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const item = list[idx];
    const itemRect = e.currentTarget.getBoundingClientRect();
    // Offset of pointer within the item (so drag doesn't "jump")
    const pointerOffX = e.clientX - itemRect.left;
    const pointerOffY = e.clientY - itemRect.top;

    setDragIdx(idx);
    let latest = { x: item.x, y: item.y };

    const onMove = (ev) => {
      // Defensive: if the mouse button is no longer pressed, the mouseup
      // listener was missed (e.g., release outside window). Force cleanup.
      if (ev && typeof ev.buttons === "number" && ev.buttons === 0) {
        onUp();
        return;
      }
      const cRect = container.getBoundingClientRect();
      if (cRect.width === 0 || cRect.height === 0) return;
      const rawX = ev.clientX - cRect.left - pointerOffX;
      const rawY = ev.clientY - cRect.top - pointerOffY;
      let nx, ny;
      if (itemUnit(item) === "px") {
        nx = Math.max(0, Math.min(cRect.width - (item.width || 0), rawX));
        ny = Math.max(0, Math.min(cRect.height - (item.height || 0), rawY));
      } else {
        nx = Math.max(0, Math.min(100 - (item.width || 0), (rawX / cRect.width) * 100));
        ny = Math.max(0, Math.min(100 - (item.height || 0), (rawY / cRect.height) * 100));
      }
      latest = { x: nx, y: ny };
      const el = container.querySelector('[data-mi-id="' + item.id + '"]');
      if (el) {
        el.style.left = nx + itemUnit(item);
        el.style.top = ny + itemUnit(item);
      }
    };

    const cleanup = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("contextmenu", onUp);
      window.removeEventListener("blur", onUp);
    };

    const onUp = () => {
      cleanup();
      setDragIdx(null);
      // Whole item body is now the drag surface, so a plain click (no move)
      // reaches here too — skip the update unless the position actually
      // changed, to avoid no-op writes / undo-history spam.
      const moved = latest.x !== list[idx].x || latest.y !== list[idx].y;
      if (onItemsUpdate && moved) {
        const next = list.slice();
        next[idx] = { ...list[idx], x: latest.x, y: latest.y };
        onItemsUpdate(next);
      }
    };

    // Defensive: if a previous drag leaked listeners, remove them first.
    cleanup();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("contextmenu", onUp);
    window.addEventListener("blur", onUp);
  };

  const beginResize = (idx, corner, e) => {
    if (visor) return;
    e.stopPropagation();
    e.preventDefault();

    const container = containerRef.current;
    if (!container) return;
    const cRect0 = container.getBoundingClientRect();
    if (cRect0.width === 0 || cRect0.height === 0) return;

    const item = list[idx];
    const unit = itemUnit(item);
    const startX = e.clientX;
    const startY = e.clientY;
    const initial = { x: item.x, y: item.y, width: item.width, height: item.height };
    let latest = { ...initial };

    const MIN_PX = 12;

    const onMove = (ev) => {
      // Defensive: if the mouse button is no longer pressed, the mouseup
      // listener was missed (e.g., release outside window). Force cleanup.
      if (ev && typeof ev.buttons === "number" && ev.buttons === 0) {
        onUp();
        return;
      }
      const cRect = container.getBoundingClientRect();
      if (cRect.width === 0 || cRect.height === 0) return;
      const dxPx = ev.clientX - startX;
      const dyPx = ev.clientY - startY;
      const dx = unit === "px" ? dxPx : (dxPx / cRect.width) * 100;
      const dy = unit === "px" ? dyPx : (dyPx / cRect.height) * 100;

      let x = initial.x;
      let y = initial.y;
      let w = initial.width;
      let h = initial.height;

      if (corner === "nw") {
        x = initial.x + dx;
        y = initial.y + dy;
        w = initial.width - dx;
        h = initial.height - dy;
      } else if (corner === "ne") {
        y = initial.y + dy;
        w = initial.width + dx;
        h = initial.height - dy;
      } else if (corner === "sw") {
        x = initial.x + dx;
        w = initial.width - dx;
        h = initial.height + dy;
      } else if (corner === "se") {
        w = initial.width + dx;
        h = initial.height + dy;
      }

      const minSize = unit === "px" ? MIN_PX : (MIN_PX / cRect.width) * 100;
      if (w < minSize) {
        if (corner === "nw" || corner === "sw") x = initial.x + initial.width - minSize;
        w = minSize;
      }
      if (h < minSize) {
        if (corner === "nw" || corner === "ne") y = initial.y + initial.height - minSize;
        h = minSize;
      }

      const maxX = unit === "px" ? cRect.width : 100;
      const maxY = unit === "px" ? cRect.height : 100;
      x = Math.max(0, Math.min(maxX - w, x));
      y = Math.max(0, Math.min(maxY - h, y));

      latest = { x, y, width: w, height: h };

      const el = container.querySelector('[data-mi-id="' + item.id + '"]');
      if (el) {
        el.style.left = x + unit;
        el.style.top = y + unit;
        el.style.width = w + unit;
        el.style.height = h + unit;
      }
    };

    const cleanup = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("contextmenu", onUp);
      window.removeEventListener("blur", onUp);
    };

    const onUp = () => {
      cleanup();
      if (onItemsUpdate) {
        const next = list.slice();
        next[idx] = { ...list[idx], ...latest };
        onItemsUpdate(next);
      }
    };

    // Defensive: if a previous resize leaked listeners, remove them first.
    cleanup();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("contextmenu", onUp);
    window.addEventListener("blur", onUp);
  };

  return (
    <div
      ref={containerRef}
      className="guide-multi-input"
      data-visor={visor ? "true" : "false"}
      data-cue-beacon={visor && cue.beacon ? "true" : "false"}
      data-cue-backdrop={visor && cue.backdrop ? "true" : "false"}
      style={{
        pointerEvents: "auto",
        cursor: "default",
      }}
    >
      {!visor && (
        <div className="guide-multi-input__badge">
          <i className="material-icons">playlist_add_check</i>
          {i18n.t("GuideMultiInput.PluginName") || "Multi-Input"}
          {list.length > 0 ? " · " + list.length : ""}
        </div>
      )}
      {!visor && list.length === 0 && (
        <div className="guide-multi-input__empty-hint">
          {i18n.t("GuideMultiInput.EmptyHint") || "Multi-Input area — add items from the toolbar"}
        </div>
      )}
      {list.map((item, idx) => {
        const state = getState(idx);
        const status = statuses[item.id] || "idle";
        const borderColor =
          state === "done"
            ? "#22c55e"
            : status === "wrong" && state === "active"
              ? "#ef4444"
              : item.kind === "tag"
                ? item.color || "#a855f7"
                : state === "active"
                  ? item.color || (item.kind === "click" ? "#ff00a0" : "#3b82f6")
                  : "#9ca3af";

        const isDragging = dragIdx === idx;

        return (
          <div
            key={item.id || idx}
            className="guide-multi-input__overlay"
            data-mi-id={item.id}
            data-state={state}
            data-kind={item.kind}
            style={{
              ...itemStyle(item),
              borderColor,
              borderStyle:
                item.kind === "click" ? "dashed" : item.kind === "tag" ? "dotted" : "solid",
              cursor: visor
                ? item.kind === "click" && state === "active"
                  ? "pointer"
                  : "default"
                : isDragging
                  ? "grabbing"
                  : "move",
              outline: isDragging ? "2px solid #7c3aed" : "none",
              pointerEvents: "auto",
            }}
            onClick={visor && item.kind === "click" ? () => handleClickItem(idx) : undefined}
            onMouseDown={!visor ? (e) => beginDrag(idx, e) : undefined}
          >
            {!visor && (
              // Visual badge only — drag is handled on the whole item body
              // (overlay onMouseDown), so a plain mousedown here bubbles up and
              // the offset is computed against the item, not this tiny corner.
              <div className="mi-drag-handle">{idx + 1}</div>
            )}
            {!visor && selectedIdx === idx && (
              <button
                type="button"
                className="mi-delete-handle"
                title={i18n.t("GuideMultiInput.delete") || "Sil"}
                // Stop the mousedown from bubbling to the body drag handler,
                // otherwise clicking × would start a drag instead of deleting.
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteItem(idx);
                }}
              >
                ×
              </button>
            )}
            {!visor && (
              <>
                <div
                  className="mi-resize-handle mi-resize-handle--nw"
                  onMouseDown={(e) => beginResize(idx, "nw", e)}
                />
                <div
                  className="mi-resize-handle mi-resize-handle--ne"
                  onMouseDown={(e) => beginResize(idx, "ne", e)}
                />
                <div
                  className="mi-resize-handle mi-resize-handle--sw"
                  onMouseDown={(e) => beginResize(idx, "sw", e)}
                />
                <div
                  className="mi-resize-handle mi-resize-handle--se"
                  onMouseDown={(e) => beginResize(idx, "se", e)}
                />
              </>
            )}
            {item.label && (
              <div className="guide-multi-input__label">
                {idx + 1} · {item.label}
              </div>
            )}
            {!item.label && <div className="guide-multi-input__label">{idx + 1}</div>}
            {visor && item.kind === "click" && state === "active" && (
              <div className="guide-multi-input__demo-hint">
                {i18n.t("GuideMultiInput.click_here") || "Click here"}
              </div>
            )}
            {item.kind === "input" && (
              <input
                type={item.inputType === "password" ? "password" : "text"}
                value={values[item.id] || ""}
                disabled={state !== "active" || !visor}
                placeholder={
                  visor
                    ? i18n.t("GuideMultiInput.placeholder") || "Type here…"
                    : (item.validation && item.validation.value) || ""
                }
                onChange={(e) => setValues((v) => ({ ...v, [item.id]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleInputSubmit(idx);
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            )}
            {item.kind === "tag" && (
              <div
                className="guide-multi-input__tag"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  color: item.color || "#7c3aed",
                  pointerEvents: "none",
                }}
              >
                {(() => {
                  const ref = item.tagRef;
                  const v = ref && effectiveTagValues ? effectiveTagValues[ref] : undefined;
                  if (v != null && v !== "") return v;
                  if (ref) return "{" + ref + "}";
                  return "—";
                })()}
              </div>
            )}
          </div>
        );
      })}
      {visor && cue.cursor && cursorPos && (
        <div className="guide-mi-cursor" style={cursorPos} aria-hidden="true">
          <svg width="26" height="30" viewBox="0 0 26 30">
            <path
              d="M2 1 L2 24 L8.5 18.5 L12.5 28 L16.5 26 L12.5 17 L21 16.5 Z"
              fill="#111827"
              stroke="#fff"
              strokeWidth="1.6"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
